import React, { type PropsWithChildren, useCallback, useMemo } from 'react';
import noop from 'lodash/noop';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { FieldDescriptionIcon } from '@atlassian/jira-issue-field-description-icon/src/ui/index.tsx';
import { FieldEditIcon } from '@atlassian/jira-issue-field-edit-icon/src/FieldEditIcon.tsx';
import { isFieldEditable } from '@atlassian/jira-issue-field-edit-icon/src/utils.tsx';
import { FieldHeading } from '@atlassian/jira-issue-field-heading/src/index.tsx';
import {
	FieldHeadingIconsContainer,
	FieldHeadingTitle,
	FieldWrapper,
	SideBySideField,
} from '@atlassian/jira-issue-field-heading/src/styled.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { FieldPin } from '@atlassian/jira-issue-field-pin/src/index.tsx';
import type { Area } from '@atlassian/jira-issue-view-common-types/src/connect-field-type.tsx';
import getShowPinButton from '@atlassian/jira-issue-view-common-utils/src/get-show-pin-button/index.tsx';
import { useEditFieldConfigContext } from '@atlassian/jira-issue-view-services/src/edit-field-config-context/context.tsx';
import { useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	useCanAdministerJira,
	useCanAdministerProject,
} from '@atlassian/jira-project-permissions-service/src/main.tsx';
import type { ui_issueViewLayoutFieldHeading_IssueViewFieldHeading$key as IssueViewFieldHeadingFragment } from '@atlassian/jira-relay/src/__generated__/ui_issueViewLayoutFieldHeading_IssueViewFieldHeading.graphql';

export type IssueViewFieldHeadingProps = PropsWithChildren<{
	testId?: string;
	area?: string;
	fragmentKey: IssueViewFieldHeadingFragment;
}>;

export const IssueViewFieldHeading = ({
	children,
	area,
	fragmentKey,
	testId,
}: IssueViewFieldHeadingProps) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const showPinButton = getShowPinButton(area as Area);
	const { overriding } = useInlineEditFieldInjections();
	const { onEditFieldConfigClick } = useEditFieldConfigContext();

	let isJiraAdmin = false;
	let isProjectAdmin = false;

	let handleClickFieldEdit = useCallback(noop, []);

	const data = useFragment<IssueViewFieldHeadingFragment>(
		graphql`
			fragment ui_issueViewLayoutFieldHeading_IssueViewFieldHeading on JiraIssueField
			@argumentDefinitions(
				isIssueViewFieldConfigEditEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/relay-migration-issue-fields-custom-field-config.relayprovider"
				}
			) {
				name
				fieldId
				description
				issue @include(if: $isIssueViewFieldConfigEditEnabled) {
					key
				}
			}
		`,
		fragmentKey,
	);

	if (fg('issue_view_field_config_edit')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		handleClickFieldEdit = useCallback(
			() => onEditFieldConfigClick(data.fieldId),
			[data.fieldId, onEditFieldConfigClick],
		);
		if (data?.issue?.key) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const projectKey = useProjectKey(data.issue.key);
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isJiraAdmin = useCanAdministerJira(projectKey);
			// eslint-disable-next-line react-hooks/rules-of-hooks
			isProjectAdmin = useCanAdministerProject(projectKey);
		}
	}

	const label = overriding.overrideLabel(data.name);
	const description = overriding.overrideDescription(data.description);

	const showEditIcon = useMemo(() => {
		if (!fg('issue_view_field_config_edit')) {
			return false;
		}
		return isFieldEditable(data.fieldId);
	}, [data.fieldId]);

	return (
		<FieldWrapper data-testid={testId}>
			<FieldHeading fieldId={data.fieldId}>
				<FieldHeadingTitle>{label}</FieldHeadingTitle>
				<FieldHeadingIconsContainer
					isCustomFieldConfigurationEnabled={fg('issue_view_field_config_edit')}
				>
					{description && <FieldDescriptionIcon description={description} />}
					{showPinButton && <FieldPin fieldId={data.fieldId} label={label} />}
					{showEditIcon && (
						<FieldEditIcon
							onClick={handleClickFieldEdit}
							hasEditPermission={isJiraAdmin || isProjectAdmin}
						/>
					)}
				</FieldHeadingIconsContainer>
			</FieldHeading>
			<SideBySideField>{children}</SideBySideField>
		</FieldWrapper>
	);
};
